import get from "lodash/get";
import set from "lodash/set";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isFunction from "lodash/isFunction";
import { isSeniorPromoCode, safeEval, verifyPhoneNumberError } from "utils/helpers";

export const emailRegex =
  "^[a-z0-9]+([-_a-z0-9]+){0,}([\\.|+|~][-_a-z0-9]+){0,}@((?:[-a-z0-9]+\\.)+[a-z]{2,63})$";

const required = ({ formValue, message }) => {
  return formValue || typeof formValue === "number" ? undefined : message;
};

const empty = ({ formValue, message }) => {
  return isEmpty(formValue) ? undefined : message;
};

const email = props =>
  regex({
    ...props,
    value: emailRegex
  });

const kanji_name = props =>
  regex({
    ...props,
    value: "^[一-龯]+$"
  });

const kanji_name_with_space = props =>
  regex({
    ...props,
    value: "^[一-龯]+(([',. -][一-龯])?[一-龯]*)*$"
  });

const kanji_name_with_alphabets_and_space = props =>
  regex({
    ...props,
    value: "^[一-龯a-zA-Z]+(([',. -][一-龯a-zA-Z])?[一-龯a-zA-Z]*)*$"
  });

const kanji_name_with_alphabets = props =>
  regex({
    ...props,
    value: "^[一-龯a-zA-Z]+$"
  });

const kana_name_with_alphabets = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ンa-zA-Z]+$"
  });

const kana_name = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ン]+$"
  });

const kana_name_with_space = props =>
  regex({
    ...props,
    value: "^[ぁ-んァ-ン]+(([',. -][ぁ-んァ-ン])?[ぁ-んァ-ン]*)*$"
  });

const kana_name_with_alphabets_and_space = props =>
  regex({
    ...props,
    value:
      "^[ぁ-んァ-ンa-zA-Z]+(([',. -][ぁ-んァ-ンa-zA-Z])?[ぁ-んァ-ンa-zA-Z]*)*$"
  });

const alphabets_and_space = props =>
  regex({
    ...props,
    value: "^[a-zA-Z]+(([',. -][a-zA-Z ])?[a-zA-Z]*)*$"
  });

// For first/middle/last names
const alphabets_and_special_chars = props =>
  regex({
    ...props,
    value: "^[-a-zA-Z()/\\\\.,'‘’` ]*$"
  });

const min_value = ({ formValue, value, message }) => {
  return formValue && formValue < value ? message : undefined;
};

const regex = ({ formValue, message, value, regex }) =>
  formValue && !new RegExp(value || regex, "i").test(formValue)
    ? message
    : undefined;

const length_between = ({
  formValues,
  formValue,
  message,
  min_length,
  max_length
}) => {
  const minLength = safeEval(get, formValues, min_length);
  const maxLength = safeEval(get, formValues, max_length);
  const formValueLength = formValue ? formValue.length : 0;
  if (minLength && formValueLength < minLength) {
    return message;
  } else if (maxLength && formValueLength > maxLength) {
    return message;
  } else {
    return undefined;
  }
};

const port_in_number = async ({ formValue, field, regex, message, props }) => {
  const prevFieldValue = get(props, ["formValues", field]);
  const orderRef = get(props, "orderRef", "");
  if (regex) {
    const validationRegex = RegExp(regex);
    const {
      validatePortInNumber,
      resetPortInNumberValidation,
      portInNumberValidation = {}
    } = props;

    if (validationRegex.test(formValue)) {
      if (prevFieldValue !== formValue) {
        isFunction(validatePortInNumber) &&
          validatePortInNumber(formValue, orderRef);
        return undefined;
      } else {
        const { valid, message: validationMessage } = portInNumberValidation;
        return !valid ? validationMessage : undefined;
      }
    } else {
      isFunction(resetPortInNumberValidation) && resetPortInNumberValidation();
      return message;
    }
  }
};

const postal_code = async ({ formValue, field, regex, message, props }) => {
  const prevFieldValue = get(props, ["formValues", field]);

  if (!prevFieldValue && !formValue) {
    return undefined;
  }

  if (regex) {
    const validationRegex = RegExp(regex);
    const {
      validatePostalCode,
      resetPostalCodeValidation,
      postalCodeValidation = {}
    } = props;

    if (validationRegex.test(formValue)) {
      if (prevFieldValue !== formValue) {
        isFunction(validatePostalCode) && validatePostalCode(formValue, field);
        return undefined;
      } else {
        const { valid } = get(postalCodeValidation, [field], {});
        if (isNil(valid)) return undefined;

        return !isEmpty(postalCodeValidation) && !valid ? message : undefined;
      }
    } else {
      isFunction(resetPostalCodeValidation) && resetPostalCodeValidation(field);
      return message;
    }
  }
};

export const nric_singapore = ({ formValue, regex, message }) => {
  let theAlpha;
  let weight;
  let parsedStr = formValue;

  if (parsedStr.length !== 9) {
    return message;
  }
  parsedStr = parsedStr.toUpperCase();

  const icArray = [];
  let i = 0;
  while (i < 9) {
    icArray[i] = parsedStr.charAt(i);
    i++;
  }
  icArray[1] = parseInt(icArray[1], 10) * 2;
  icArray[2] = parseInt(icArray[2], 10) * 7;
  icArray[3] = parseInt(icArray[3], 10) * 6;
  icArray[4] = parseInt(icArray[4], 10) * 5;
  icArray[5] = parseInt(icArray[5], 10) * 4;
  icArray[6] = parseInt(icArray[6], 10) * 3;
  icArray[7] = parseInt(icArray[7], 10) * 2;
  weight = 0;
  i = 1;
  while (i < 8) {
    weight += icArray[i];
    i++;
  }
  const offset =
    icArray[0] === "T" || icArray[0] === "G" ? 4 : icArray[0] === "M" ? 3 : 0;
  let temp = (offset + weight) % 11;
  if (icArray[0] === "M") temp = 10 - temp;

  const st = ["J", "Z", "I", "H", "G", "F", "E", "D", "C", "B", "A"];
  const fg = ["X", "W", "U", "T", "R", "Q", "P", "N", "M", "L", "K"];
  const m = ["K", "L", "J", "N", "P", "Q", "R", "T", "U", "W", "X"];
  theAlpha = 0;
  if (icArray[0] === "S" || icArray[0] === "T") {
    theAlpha = st[temp];
  } else if (icArray[0] === "F" || icArray[0] === "G") {
    theAlpha = fg[temp];
  } else if (icArray[0] === "M") {
    theAlpha = m[temp];
  }

  if (icArray[8] === theAlpha) {
    return null;
  }

  return message;
};

const national_id_taiwan = ({ formValue, regex, message }) => {
  if (formValue.length !== 10 || (regex && !formValue.match(regex))) {
    return message;
  }
  const taiwanLocationCode = {
    A: "10",
    B: "11",
    C: "12",
    D: "13",
    E: "14",
    F: "15",
    G: "16",
    H: "17",
    I: "34",
    J: "18",
    K: "19",
    L: "20",
    M: "21",
    N: "22",
    O: "35",
    P: "23",
    Q: "24",
    R: "25",
    S: "26",
    T: "27",
    U: "28",
    V: "29",
    W: "32",
    X: "30",
    Y: "31",
    Z: "33"
  };

  const validateNationalId = (location, nationalId) => {
    const nationalIdLength = nationalId.length - 1;
    let totalSum =
      Number(nationalId[nationalId.length - 1]) +
      (Number(location[0]) * 1 + Number(location[1]) * 9);
    for (let i = 1; i <= 8; i++) {
      totalSum += Number(nationalId[nationalIdLength - i]) * i;
    }
    return totalSum % 10;
  };

  const nationalId = formValue.split("");
  const location = taiwanLocationCode[nationalId[0]];
  return validateNationalId(location, nationalId) === 0 ? null : message;
};

const getMinimumAge = (validations, referralCode) => {
  if (isSeniorPromoCode(referralCode)) {
    return validations.senior_value;
  } else {
    return validations.value;
  }
};

const mapping = {
  presence: required,
  empty,
  regex,
  email,
  alphabets_and_space,
  alphabets_and_special_chars,
  min_value,
  length_between,
  nric_singapore,
  national_id_taiwan,
  kanji_name,
  kanji_name_with_space,
  kanji_name_with_alphabets_and_space,
  kana_name,
  kana_name_with_alphabets_and_space,
  kana_name_with_space,
  kanji_name_with_alphabets,
  kana_name_with_alphabets
};

const asyncMapping = {
  port_in_number,
  postal_code
};

const validate = (formValues, props) => {
  const errors = {};
  const { validations = [], appSettings = {} } = props.formValidations || {};

  const validatePhoneNumber = verifyPhoneNumberError(appSettings, props)
  validations.forEach(validation => {
    const { type, field, condition, async } = validation;
    let formValue = get(formValues, field);
    const validationNeeded = safeEval(get, formValues, condition, true, props);
    // console.log("AAAAAAAA--------------------")
    // console.log("AAAAAAAAvalidationNeeded", validationNeeded)
    // console.log("AAAAAAAAformValues", formValues)
    // console.log("AAAAAAAAcondition", condition, " ::: ", props)
    // console.log("AAAAAAAAformValue", formValue)
    // console.log("AAAAAAAAtype", type)
    // console.log("AAAAAAAAfield", field)


    if (get(errors, field, false) || !validationNeeded || async) {
      return;
    }

    // custom validation lookup from various shared configuration files

    if ("lookup" in validation) {
      let sharedValidations = get(appSettings, validation.lookup);
      if ("lookup_type" in validation) {
        const lookupType = safeEval(
          get,
          formValues,
          validation.lookup_type,
          {},
          props
        );
        sharedValidations = sharedValidations[lookupType] || {};
      }
      const requiredInConfig = get(sharedValidations, "required");
      const errorMessage = get(
        sharedValidations,
        "error_msg",
        "error_cannot_be_blank"
      );
      console.log("AAAA Field3", field, "=>", sharedValidations)

      if (field === "age" || field === "other_user_age") {
        set(
          errors,
          field,
          mapping["min_value"]({
            formValue,
            value: getMinimumAge(sharedValidations, props.referralCode),
            message: errorMessage
          })
        );
      } else if (field.match(/^((contact|alternate)_number|icc_id)$/)) {
        if (validatePhoneNumber && field === "contact_number") {
          return
        }
        set(
          errors,
          field,
          mapping["regex"]({
            formValue,
            value: sharedValidations.validation_regex,
            message: errorMessage
          })
        );
      } else if (field === "nric") {
        const docType = get(formValues, "doc_type.value", {});
        validation = sharedValidations[docType] || {};

        set(
          errors,
          field,
          mapping["nric_singapore"]({
            formValue,
            regex: validation.validation_regex,
            message: validation.error_msg
          })
        );
      } else if (field.match(/^(billing|delivery)_/)) {
        const field_key = field.replace(/^(billing|delivery)_/, "");
        const validation = get(sharedValidations, `fields.${field_key}`, {});
        const required = get(validation, "required", false);

        required &&
          set(
            errors,
            field,
            mapping["presence"]({
              formValue,
              message: validation.error_msg
            })
          );
      } else {
        let validationType = sharedValidations.validation_type || type;

        validationType =
          !validationType && sharedValidations.validation_regex
            ? "regex"
            : validationType;
        const validationFunction = mapping[validationType];
        console.log("AAAA Field2", field, "=>", errors)
        if (isFunction(validationFunction)) {
          set(
            errors,
            field,
            validationFunction({
              regex: sharedValidations.validation_regex,
              message: errorMessage,
              formValues,
              formValue,
              props,
              ...validation
            })
          );
        }
      }

      if (!errors[field] && requiredInConfig) {
        set(
          errors,
          field,
          mapping["presence"]({
            formValue,
            message: errorMessage
          })
        );
      }
    } else {
      if (isFunction(mapping[type])) {
        if (validatePhoneNumber && field === "contact_number") {
          return
        }

        set(
          errors,
          field,
          mapping[type]({ formValues, formValue, ...validation })
        );
      }
    }
  });

  return errors;
};

async function asyncForEach(array, callback) {
  for (let index = 0; index < array.length; index++) {
    await callback(array[index], index, array);
  }
}

export const asyncValidate = async (formValues, dispatch, props) => {
  return new Promise(resolve => resolve()).then(async () => {
    const errors = {};
    const { validations = [], appSettings = {} } = props.formValidations || {};

    await asyncForEach(validations, async validation => {
      const { type, field, condition, async } = validation;
      let formValue = get(formValues, field);
      const validationNeeded = safeEval(
        get,
        formValues,
        condition,
        true,
        props
      );

      if (get(errors, field, false) || !validationNeeded || !async) {
        return;
      }

      if (isFunction(asyncMapping[type])) {
        const asyncMappingProps = {
          field,
          formValues,
          formValue,
          dispatch,
          props,
          ...validation
        };

        if ("lookup" in validation) {
          const sharedValidations = get(appSettings, validation.lookup);
          asyncMappingProps["regex"] = sharedValidations.validation_regex;
          asyncMappingProps["message"] = sharedValidations.error_msg;
        }

        const message = await asyncMapping[type](asyncMappingProps);
        console.log("AAAA Field", field, "=>", message)
        if (message) {
          errors[field] = message;
        }
      }
    });
    if (!isEmpty(errors)) {
      throw errors;
    }
  });
};

export default validate;
